import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string) => Promise<{ error: string | null; needsEmailConfirmation: boolean }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext(undefined as AuthContextValue | undefined);

const mapSupabaseUser = (user: any): AuthUser => ({
  uid: user.id,
  email: user.email ?? null,
  displayName: user.user_metadata?.full_name ?? user.email ?? 'Supabase User',
  photoURL: user.user_metadata?.avatar_url ?? null
});

const getAuthErrorMessage = (error: unknown, fallbackMessage: string) => {
  const message = error instanceof Error
    ? error.message
    : typeof error === 'string'
      ? error
      : typeof error === 'object' && error !== null && 'message' in error && typeof (error as { message?: unknown }).message === 'string'
        ? (error as { message: string }).message
        : '';

  if (!message) {
    return fallbackMessage;
  }

  const normalizedMessage = message.trim().toLowerCase();

  if (normalizedMessage.includes('invalid login credentials')) {
    return 'Invalid email or password.';
  }

  if (normalizedMessage.includes('failed to fetch') || error instanceof TypeError) {
    return 'Network error reaching Supabase.';
  }

  return message;
};

const debugAuthBootstrap = (label: string, details: Record<string, unknown>) => {
  if (typeof window === 'undefined') return;
  if (window.localStorage.getItem('debug_property_sync') !== '1') return;
  console.info(`[Auth Bootstrap] ${label}`, details);
};

const INIT_STEP_STORE_KEY = '__WRENNEN_INIT_STEPS__';
const recordInitStep = (label: string, details: Record<string, unknown> = {}) => {
  if (typeof window === 'undefined') return;
  const existing = (window as any)[INIT_STEP_STORE_KEY] || {};
  (window as any)[INIT_STEP_STORE_KEY] = {
    ...existing,
    [label]: {
      at: Date.now(),
      ...details
    }
  };
  console.info(label, details);
};

export const AuthProvider = (props: { children: any }) => {
  const { children } = props;
  const [user, setUser] = useState(null as AuthUser | null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    recordInitStep('INIT loading true', {
      authLoading: true,
      source: 'AuthProvider mount'
    });

    const initializeSession = async () => {
      debugAuthBootstrap('Session load start', {});
      recordInitStep('INIT 1 start session', {});

      try {
        const { data, error } = await supabase.auth.getSession();

        debugAuthBootstrap('Session load end', {
          hasSession: Boolean(data.session),
          sessionUserId: data.session?.user?.id ?? null,
          error: error?.message ?? null
        });
        recordInitStep('INIT 1 done session', {
          hasSession: Boolean(data.session),
          sessionUserId: data.session?.user?.id ?? null,
          error: error?.message ?? null
        });

        if (!isMounted) return;

        if (error) {
          setUser(null);
          return;
        }

        setUser(data.session?.user ? mapSupabaseUser(data.session.user) : null);
      } catch (error) {
        debugAuthBootstrap('Session load exception', {
          error: error instanceof Error
            ? {
                name: error.name,
                message: error.message,
                stack: error.stack ?? null
              }
            : { raw: String(error) }
        });
        recordInitStep('INIT 1 error session', {
          error: error instanceof Error
            ? {
                name: error.name,
                message: error.message,
                stack: error.stack ?? null
              }
            : { raw: String(error) }
        });

        if (!isMounted) return;
        setUser(null);
      } finally {
        if (!isMounted) return;
        debugAuthBootstrap('Session loading cleared', { loading: false });
        recordInitStep('INIT loading false', {
          authLoading: false,
          source: 'initializeSession finally'
        });
        setLoading(false);
      }
    };

    void initializeSession();

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event: string, session: any) => {
      debugAuthBootstrap('Auth state changed', {
        event: _event,
        hasSession: Boolean(session),
        sessionUserId: session?.user?.id ?? null
      });
      recordInitStep('INIT auth state changed', {
        event: _event,
        hasSession: Boolean(session),
        sessionUserId: session?.user?.id ?? null
      });
      setUser(session?.user ? mapSupabaseUser(session.user) : null);
      recordInitStep('INIT loading false', {
        authLoading: false,
        source: 'onAuthStateChange'
      });
      setLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    console.info('[Auth] signIn called', {
      email,
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL ?? null
    });
    console.info('[Auth] preflight check', {
      signIn: false
    });

    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      console.info('[Auth] signIn Supabase result', {
        userId: data.user?.id ?? null,
        sessionPresent: Boolean(data.session),
        error
      });

      if (error) {
        return { error: getAuthErrorMessage(error, 'Unable to sign in.') };
      }

      return { error: null };
    } catch (error) {
      console.error('[Auth] signIn raw failure', error);
      return {
        error: getAuthErrorMessage(error, 'Unable to sign in.')
      };
    }
  };

  const signUp = async (email: string, password: string) => {
    console.info('[Auth] signUp called', {
      email,
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL ?? null
    });
    console.info('[Auth] preflight check', {
      signUp: false
    });

    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      console.info('[Auth] signUp Supabase result', {
        userId: data.user?.id ?? null,
        sessionPresent: Boolean(data.session),
        needsEmailConfirmation: Boolean(data.user && !data.session),
        error
      });

      if (error) {
        return {
          error: getAuthErrorMessage(error, 'Unable to create account.'),
          needsEmailConfirmation: false
        };
      }

      return {
        error: null,
        needsEmailConfirmation: Boolean(data.user && !data.session)
      };
    } catch (error) {
      console.error('[Auth] signUp raw failure', error);
      return {
        error: getAuthErrorMessage(error, 'Unable to create account.'),
        needsEmailConfirmation: false
      };
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      debugAuthBootstrap('Sign out exception', {
        error: error instanceof Error
          ? {
              name: error.name,
              message: error.message,
              stack: error.stack ?? null
            }
          : { raw: String(error) }
      });
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
